import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "./backendconfig";
import { usePublicList } from "./usePublicList";
import "./FreeDemo.css";

const backend = BACKEND_URL.replace(/\/+$/, "");
const PURCHASE_URL = "https://www.embroiderydesigncourse.com/courses/";

function resolveBackendUrl(path) {
  if (typeof path !== "string" || !path.trim()) return "";

  try {
    const url = new URL(path.trim(), `${backend}/`);

    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}

function DemoVideoCard({ item, playing, onPlay }) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const thumbnailUrl = resolveBackendUrl(item.thumbnail);

  useEffect(() => {
    setThumbnailFailed(false);
  }, [thumbnailUrl]);

  const params = new URLSearchParams({
    id: String(item.id),
    title: item.title || "Free Demo",
    embed: "1",
  });

  return (
    <article className="ltc-free-demo-player">
      <div className="ltc-free-demo-screen">
        {playing ? (
          <iframe
            src={`${backend}/watch.php?${params.toString()}`}
            title={item.title}
            className="ltc-free-demo-iframe"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="ltc-free-demo-preview"
            onClick={onPlay}
            aria-label={`Watch ${item.title}`}
          >
            {thumbnailUrl && !thumbnailFailed && (
              <img
                src={thumbnailUrl}
                alt=""
                className="ltc-free-demo-thumbnail"
                loading="lazy"
                decoding="async"
                onError={() => setThumbnailFailed(true)}
              />
            )}

            <span className="ltc-free-demo-preview-overlay">
              <span
                className="ltc-free-demo-play-icon"
                aria-hidden="true"
              >
                ▶
              </span>

              <span className="ltc-free-demo-play-label">
                Watch Free Demo
              </span>
            </span>
          </button>
        )}
      </div>

      <div className="ltc-free-demo-video-copy">
        <span className="ltc-free-demo-free-badge">FREE DEMO</span>

        <h3 className="ltc-free-demo-video-title">
          {item.title}
        </h3>
      </div>
    </article>
  );
}

export default function FreeDemo() {
  const { items, loading, error } = usePublicList(
    `${backend}/media.php?type=free-demo`
  );

  // null means show all videos.
  const [selectedId, setSelectedId] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selected = items.find(
    (item) => String(item.id) === selectedId
  );

  const showAll = selectedId === null || !selected;

  const visibleVideos = showAll ? items : [selected];

  const selectTopic = (id) => {
    setPlayingId(null);
    setSelectedId(id === null ? null : String(id));
  };

  return (
    <main className="ltc-free-demo-page">
      <section className="ltc-free-demo-section">
        <div className="ltc-free-demo-container">
          <header className="ltc-free-demo-heading">
            <Link
              to="/#courses"
              className="ltc-free-demo-back-link"
            >
              ← Back to Courses
            </Link>

            <span className="ltc-free-demo-eyebrow">
              WATCH & LEARN
            </span>

            <h1 className="ltc-free-demo-title">
              Free Demo Classes
            </h1>

            <p className="ltc-free-demo-description">
              Explore all free demo videos or select a topic from
              the sidebar.
            </p>
          </header>

          {loading && (
            <p className="ltc-free-demo-message" role="status">
              Loading demo topics…
            </p>
          )}

          {error && (
            <p
              className="ltc-free-demo-message ltc-free-demo-message-error"
              role="alert"
            >
              {error}
            </p>
          )}

          {!loading && !error && items.length === 0 && (
            <p className="ltc-free-demo-message">
              No demo videos available yet.
            </p>
          )}

          {!loading && !error && items.length > 0 && (
            <div className="ltc-free-demo-layout">
              <aside
                className="ltc-free-demo-topics"
                aria-label="Free demo topics"
              >
                <h2 className="ltc-free-demo-topics-heading">
                  Demo Topics
                </h2>

                <nav
                  className="ltc-free-demo-topic-list"
                  aria-label="Select a demo video"
                >
                  <button
                    type="button"
                    className={`ltc-free-demo-topic-button${
                      showAll
                        ? " ltc-free-demo-topic-button-active"
                        : ""
                    }`}
                    aria-pressed={showAll}
                    onClick={() => selectTopic(null)}
                  >
                    <span className="ltc-free-demo-topic-number">
                      All
                    </span>

                    <span className="ltc-free-demo-topic-title">
                      {/* All Videos ({items.length}) */}
                         All Videos 
                    </span>
                  </button>

                  {items.map((item, index) => {
                    const active =
                      !showAll &&
                      String(item.id) === selectedId;

                    return (
                      <button
                        type="button"
                        key={item.id}
                        className={`ltc-free-demo-topic-button${
                          active
                            ? " ltc-free-demo-topic-button-active"
                            : ""
                        }`}
                        aria-pressed={active}
                        onClick={() => selectTopic(item.id)}
                      >
                        <span className="ltc-free-demo-topic-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="ltc-free-demo-topic-title">
                          {item.title}
                        </span>
                      </button>
                    );
                  })}

                  <div className="ltc-free-demo-sidebar-purchase">
                    <p>Ready to learn the complete course?</p>

                    <Link
                       to="/#courses"
                        state={{ courseCategory: "all" }}
                       className="ltc-free-demo-buy-button"
                    >
                    Buy Now ↗
                     </Link>
                  </div>
                </nav>
              </aside>

              <div className="ltc-free-demo-content">
                <div className="ltc-free-demo-content-heading">
                  <h2>
                    {showAll ? "All Demo Videos" : selected.title}
                  </h2>

                  <span>
                    {visibleVideos.length}{" "}
                    {visibleVideos.length === 1 ? "video" : "videos"}
                  </span>
                </div>

                <div
                  className={`ltc-free-demo-video-grid${
                    showAll ? "" : " ltc-free-demo-video-grid-single"
                  }`}
                >
                  {visibleVideos.map((item) => (
                    <DemoVideoCard
                      key={item.id}
                      item={item}
                      playing={playingId === String(item.id)}
                      onPlay={() =>
                        setPlayingId(String(item.id))
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}